import {
  AxisTick,
  BidPackageRow,
  BidPackageRowGeometry,
  DependencyModel,
  Geometry,
  RowSegmentGeometry
} from "./model";
import { RenderConfig } from "./render-config";
import { computeDateScale, DateScale, DateScaleOptions } from "./date-scale";

// 2 px per day keeps duration bars readable without dominating the proportional axis.
const SEGMENT_PX_PER_DAY = 2;

function rowGeometry(
  row: BidPackageRow,
  y: number,
  scale: DateScale,
  config: RenderConfig,
  milestoneX: (id: string) => number | null
): BidPackageRowGeometry {
  const linkBadge = row.linkedDpMilestone
    ? {
        milestoneId: row.linkedDpMilestone.id,
        milestoneName: row.linkedDpMilestone.name,
        milestoneX: milestoneX(row.linkedDpMilestone.id)
      }
    : null;

  if (row.state === "NO_PLAN") {
    return {
      row,
      y,
      bar: null,
      milestoneTicks: [],
      governingBox: null,
      durationSegments: [],
      linkBadge,
      placeholder: { text: config.noPlanText }
    };
  }

  const xs = row.milestones
    .map((m) => scale.x(m.effectiveDate))
    .filter((x): x is number => x !== null);
  let bar: { x: number; width: number } | null = null;
  if (xs.length > 0) {
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    bar = { x: minX, width: maxX - minX };
  }

  const milestoneTicks = row.milestones
    .map((node) => ({ node, x: scale.x(node.effectiveDate) }))
    .filter((t): t is { node: typeof t.node; x: number } => t.x !== null);

  const govX = scale.x(row.governing?.startDate ?? null);
  const governingBox = govX !== null ? { x: govX } : null;

  // bar is non-null for PLANNED rows (adapter guarantees hasDates); 0 is a safe
  // fallback for type completeness only.
  let cursor = bar ? bar.x + bar.width : 0;
  const durationSegments: RowSegmentGeometry[] = row.durations.map(
    (segment) => {
      const width = segment.days * SEGMENT_PX_PER_DAY;
      const seg = { x: cursor, width, segment };
      cursor += width;
      return seg;
    }
  );

  return {
    row,
    y,
    bar,
    milestoneTicks,
    governingBox,
    durationSegments,
    linkBadge,
    placeholder: null
  };
}

export function computeGeometry(
  model: DependencyModel,
  config: RenderConfig,
  // When rendering multiple DPs on a shared project-wide axis, callers pass a
  // pre-built scale so all DPs are positioned against the same pixel domain.
  injectedScale?: DateScale,
  scaleOptions?: DateScaleOptions
): Geometry {
  const designDates = model.designPackage.milestones
    .map((m) => m.effectiveDate)
    .filter((d): d is string => !!d);
  const scale =
    injectedScale ??
    computeDateScale(model.axisDates, designDates, scaleOptions);

  // id → x of each DP milestone tick, so a row can position its linked milestone.
  const idToX = new Map(
    model.designPackage.milestones.map((m) => [m.id, scale.x(m.effectiveDate)])
  );
  const milestoneX = (id: string) => idToX.get(id) ?? null;

  const rows = model.bidPackages.map((row, i) =>
    rowGeometry(
      row,
      config.axisHeight + i * config.rowHeight,
      scale,
      config,
      milestoneX
    )
  );

  // Milestones that at least one bid package links into — used to accent their
  // diamonds in the header.
  const linkedIds = new Set(
    rows.map((r) => r.linkBadge?.milestoneId).filter((id): id is string => !!id)
  );

  const ticks: AxisTick[] = model.designPackage.milestones
    .map((milestone) => ({
      milestone,
      x: scale.x(milestone.effectiveDate),
      isLinkTarget: linkedIds.has(milestone.id)
    }))
    .filter((t): t is AxisTick => t.x !== null);

  // Milestones with no committed date have no x, so they can't sit on the axis.
  // They are carried through for the tray the renderer draws beneath the
  // timeline. Unnamed milestones are internal placeholders, never shown.
  const undatedMilestones = model.designPackage.milestones.filter(
    (m) => !m.effectiveDate && Boolean(m.name)
  );

  return {
    width: config.labelColumnWidth + scale.contentWidth + config.clusterWidth,
    height: config.axisHeight + model.bidPackages.length * config.rowHeight,
    axisHeight: config.axisHeight,
    axis: { ticks },
    undatedMilestones,
    rows,
    breaks: scale.breaks,
    inScope: scale.inScope,
    outsideZones: scale.outsideZones,
    gridlines: scale.gridlines,
    weekTicks: scale.weekTicks,
    xToDate: scale.xToDate,
    todayDate: model.designPackage.todayDate ?? null,
    currentDelay: model.designPackage.currentDelay ?? null
  };
}
